import Vue from 'vue';
import Vuex from 'vuex';

import user from './user';
import jobs from './jobs';
import entities from './entities';
import comments from './comments';
import accounts from './accounts';
import live from './live';
import billing from './billing';
import datacart from './datacart';
import preferences from './preferences';
import { getInstance } from '../auth';
import { queryUserClient } from '../clients';
import { AccountProjectRequest } from '../protobuf/service_query_pb';

Vue.use(Vuex);

export function emptyState() {
  return {
    activeWorkspaceId: undefined,
    accounts: {}, // id to account
    projects: {}, // id to project
    activeProjectId: undefined, // this will be set by a route guard. this should only be set to a projectId IF there is one in the route.
    fallbackProjectId: undefined, // will be set to the first project seen. used if no route or default project id is found in route guard.
    initialLoadComplete: false, // the initial load will be fal
    loadError: undefined, // if an error occurred during a root load, it will be stored here.
    userOwnsNoAccount: true, // this is set to false on the load of user accounts if dto flags it that way
    darkMode: window.localStorage.getItem('darkMode'),
  };
}

export const rootGetters = {
  isInitialLoadComplete: (state) => state.initialLoadComplete,
  loadError: (state) => state.loadError,
  fallbackProjectId: (state) => state.fallbackProjectId,
  accountById: (state) => (id) => state.accounts[id],
  projectById: (state) => (id) => state.projects[id],
  accounts: (state) => state.accounts,
  projects: (state) => state.projects,
  activeProjectId: (state) => (state.activeProjectId), // the active project is the one in the route with a fallback value of the default.
  activeProject: (state) => state.projects[state.activeProjectId],
  activeProjectName: (state) => (state.projects[state.activeProjectId] ? state.projects[state.activeProjectId].name : undefined),
  activeAccountId: (state) => state.activeWorkspaceId,
  activeWorkspaceId: (state) => state.activeWorkspaceId,
  activeWorkspaceName: (state) => (state.accounts[state.activeWorkspaceId] ? state.accounts[state.activeWorkspaceId].name : undefined),
  // stats
  activeProjectJobCount: (state) => (state.projects[state.activeProjectId] ? state.projects[state.activeProjectId].jobsCount : 0),
  activeProjectMediaCount: (state) => (state.projects[state.activeProjectId] ? state.projects[state.activeProjectId].mediaCount : 0),
  activeProjectByteCount: (state) => (state.projects[state.activeProjectId] ? state.projects[state.activeProjectId].spaceUsed : 0),
  activeProjectFileCount: (state) => (state.projects[state.activeProjectId] ? state.projects[state.activeProjectId].totalFileCount : 0),
  activeProjectFolderCount: (state) => (state.projects[state.activeProjectId] ? state.projects[state.activeProjectId].totalFolderCount : 0),
  userOwnsNoAccount: (state) => state.userOwnsNoAccount,
  lastActiveProjectId: (state) => {
    const lastActive = window.localStorage.getItem('lastActiveProjectId');
    if (lastActive) {
      if (!state.projects[lastActive]) {
        // only allow the last active to be used if it actually is a project the user has perms to.
        console.warn('last active project id was not found in users project list. ignoring as default.');
      } else {
        return lastActive;
      }
    }
    return undefined;
  },
  lastActiveWorkspaceId: (state) => {
    const lastActive = window.localStorage.getItem('lastActiveWorkspaceId');
    if (lastActive) {
      if (!state.accounts[lastActive]) {
        // only allow the last active to be used if it actually is a project the user has perms to.
        console.warn('last active workspace id was not found in users workspace list. ignoring as default.');
      } else {
        return lastActive;
      }
    }
    return undefined;
  },
  isDarkModeOn: (state) => state.darkMode,
};

export const mutations = {
  /**
   * Set a flag to indicate that the user is signed in and all initial queries for app use have been completed.
   */
  setInitialLoadComplete: (state) => {
    state.initialLoadComplete = true;
    console.info('setInitialLoadComplete');
  },
  setErrorOnLoad: (state, error) => {
    state.loadError = error;
    console.error('setErrorOnLoad', error);
  },
  addAccount: (state, account) => {
    Vue.set(state.accounts, account.accountId, account);
    console.info('loaded workspace:', account);
  },
  addProject: (state, project) => {
    Vue.set(state.projects, project.projectId, project);
    console.log('loaded project:', project);
    if (!state.fallbackProjectId) {
      state.fallbackProjectId = project.projectId; // fallback is first seen
      console.log('fallback project set to', state.fallbackProjectId);
    }
  },
  removeProject: (state, projectId) => {
    if (state.projects[projectId]) {
      Vue.delete(state.projects, projectId);
      console.info('removed project', projectId);
    }
  },
  /* setRouteWorkspaceId
  NEVER CALL setRouteWorkspaceId OUTSIDE OF ROUTER
  This will unset the active project if a workspace change occurs.
  Call this BEFORE a project change.
   */
  setRouteWorkspaceId: (state, workspaceId) => {
    if (!state.accounts[workspaceId]) {
      console.error('cannot set active workspace to missing workspace', workspaceId);
      throw new Error('cannot set active workspace to missing workspace');
    }
    if (state.activeWorkspaceId !== workspaceId) {
      state.activeWorkspaceId = workspaceId;
      state.activeProjectId = undefined; // unset active project because we just switched workspaces
      window.localStorage.setItem('lastActiveWorkspaceId', workspaceId);
      console.info('set active workspace: ', state.activeWorkspaceId);
      // TODO: wipe workspace dependent state here
    }
  },
  setDarkMode: (state, darkMode) => {
    state.darkMode = darkMode;
    console.info('set darkMode status: ', darkMode);
  },
};

export const actions = {
  // setRouteProjectId is an action so we can wrap several mutations.
  // this wipes any project dependent state throughout the entire vuex store. all applicable modules are reset.
  setRouteProjectId: ({ state, commit, dispatch }, projectId) => {
    const project = state.projects[projectId];
    if (!project) {
      console.error('cannot set active project to missing project', projectId);
      return;
    }

    // set workspace id before project.
    // setting workspace id unsets the active project
    const workspaceId = project.accountId;
    commit('setRouteWorkspaceId', workspaceId);
    if (state.activeProjectId !== projectId) {
      // wipe all project related state and close any project related streams
      dispatch('wipeProjectState').then(() => {
        state.activeProjectId = projectId;
        window.localStorage.setItem('lastActiveProjectId', projectId);
        // reset error message after selecting project
        state.loadError = undefined;
        console.info('set active project:', state.activeProjectId);
        // now we (re)open our project stream after the project id is set
        dispatch('live/beginProjectStream');
      });
    }
  },
  initialLoad: async ({ dispatch, commit }) => new Promise((resolve, reject) => {
    Promise.all([
      dispatch('loadUserAccounts'),
      dispatch('user/loadDetails'),
      dispatch('user/loadPendingInvites'),
      dispatch('user/loadTokenFlags'),
    ]).then((value) => {
      console.info('initial load resolved', value);
      // commit('setInitialLoadComplete');
      resolve();
    }).catch((error) => {
      console.error('initial load rejected', error);
      commit('setErrorOnLoad', error);
      reject();
    }).finally(() => commit('setInitialLoadComplete'));
  }),
  // close all project streams and clear all project dependant state
  wipeProjectState: ({ commit }) => {
    console.info('beginning reset of project state');
    commit('live/killProjectStream');
    commit('live/resetState');
    console.info('finished resetting project state');
  },
  /**
   * An initial load of a user's account -> project spread.
   */
  loadUserAccounts: async ({ commit, state }) => new Promise((resolve, reject) => {
    try {
      // eslint-disable-next-line no-underscore-dangle
      getInstance().getTokenSilently().then(async (token) => {
        const request = new AccountProjectRequest();
        const metadata = { Authorization: `bearer ${token}` };
        console.info('listing workspaces and projects', request.toObject(), metadata);
        queryUserClient.listAccountProjects(request, metadata, (err, response) => {
          if (err !== null) {
            console.log('received err from loadUserAccounts ', err);
            reject(err);
            return;
          }
          const accountsList = response.getAccountsList();
          state.userOwnsNoAccount = response.getUserOwnsNoAccount();
          for (let x = 0; x < accountsList.length; x += 1) {
            const account = accountsList[x].toObject();
            const { accountId, projectsList } = account;
            commit('addAccount', account);
            for (let y = 0; y < projectsList.length; y += 1) {
              const project = { ...projectsList[y], accountId };
              commit('addProject', project);
            }
          }
          resolve(response.toObject());
        });
      });
    } catch (error) {
      console.log('error attempting to load initial account and project data ', error);
      reject(error.message);
    }
  }),

  // eslint-disable-next-line no-unused-vars
  upload: async ({ commit, state }, { inputData, uploadUrl }) => new Promise((resolve, reject) => {
    // TODO: resolve / reject this.
    console.log('uploading to ', uploadUrl);
    const xhr = new XMLHttpRequest();
    // this.xhr = xhr; // TODO: was this assignment (this.xhr = xhr) to self necessary so xhr doesn't get garbage collected?

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const percentage = Math.round((e.loaded * 100) / e.total);
        console.info('UPLOAD PROGRESS:', percentage, e);
      }
    }, false);

    xhr.upload.addEventListener('load', (e) => {
      console.info('UPLOAD COMPLETE:', e);
    }, false);

    xhr.open('POST', uploadUrl);
    xhr.send(inputData);
  }),
  setDarkMode: async ({ commit }, darkMode) =>  new Promise((resolve, reject) => {
    commit('setDarkMode', darkMode);
    console.info('change dark mode - ', darkMode);
    resolve(darkMode);
  }),
};

export const modules = {
  entities,
  jobs,
  user,
  comments,
  accounts,
  live,
  billing,
  datacart,
  preferences,
};

export default new Vuex.Store({
  namespaced: true,
  state: emptyState(),
  getters: rootGetters,
  mutations,
  actions,
  modules,
  strict: false, // process.env.NODE_ENV !== 'production',
});
